/**
 * Balance Sheet Form Component
 * Displays balance sheet data entry table with Assets, Liabilities, and Equity sections
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const HelpTooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle size={14} className="inline text-blue-600 cursor-help" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg bottom-full left-1/2 -translate-x-1/4 mb-1 pointer-events-none">
      {text}
    </div>
  </div>
);

HelpTooltip.propTypes = {
  text: PropTypes.string.isRequired
};

const ASSETS_FIELDS = [
  {
    label: 'Cash and Cash Equivalents',
    field: 'cash',
    help: 'Money in bank accounts, money market funds, and other highly liquid assets.',
    indent: true
  },
  {
    label: 'Current Assets (excl. Cash)',
    field: 'current_assets',
    help: 'Assets convertible to cash within one year: inventory, accounts receivable, prepaid expenses.',
    indent: true
  },
  {
    label: 'Non-Current Assets',
    field: 'non_current_assets',
    help: 'Long-term assets like property, equipment, patents, and goodwill.',
    indent: true
  },
  {
    label: 'Total Assets',
    field: 'total_assets',
    help: 'Auto-calculated: Cash + Current Assets + Non-Current Assets. All resources owned by the business.',
    readonly: true,
    bold: true
  }
];

const LIABILITIES_FIELDS = [
  {
    label: 'Current Liabilities',
    field: 'short_term_debt',
    help: 'Obligations due within one year: accounts payable, short-term loans, accrued expenses.',
    indent: true
  },
  {
    label: 'Long-term Liabilities',
    field: 'long_term_debt',
    help: 'Debts and obligations due beyond one year: mortgages, bonds, long-term loans.',
    indent: true
  },
  {
    label: 'Total Liabilities',
    field: 'total_liabilities',
    help: 'Auto-calculated: Current Liabilities + Long-term Liabilities. All obligations owed.',
    readonly: true,
    bold: true
  }
];

const EQUITY_FIELDS = [
  {
    label: 'Total Equity',
    field: 'equity',
    help: "Auto-calculated: Total Assets - Total Liabilities. Owner's equity or shareholders' equity.",
    readonly: true,
    bold: true
  }
];

const BalanceSheetForm = ({
  years,
  financialData,
  formatNumberWithCommas,
  handleInputChange
}) => {
  return (
    <div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left p-3 border">Line Item</th>
            {years.map(y => <th key={y} className="p-3 border">{y}</th>)}
          </tr>
        </thead>
        <tbody>
          {/* ASSETS SECTION */}
          <tr className="bg-blue-100">
            <td colSpan={years.length + 1} className="p-2 border font-bold text-blue-900">
              ASSETS
            </td>
          </tr>
          {ASSETS_FIELDS.map((item, idx) => (
            <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className={`p-3 border font-medium ${item.readonly ? 'bg-blue-50' : ''} ${item.bold ? 'font-bold' : ''}`}>
                {item.indent && <span className="ml-4">↳ </span>}
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
                {item.readonly && <span className="ml-2 text-xs text-green-600">(Auto)</span>}
              </td>
              {years.map(year => (
                <td key={year} className={`p-2 border ${item.readonly ? 'bg-blue-50' : ''}`}>
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className={`w-full px-2 py-1 border rounded ${
                      item.readonly ? 'bg-gray-100 cursor-not-allowed font-semibold' : ''
                    }`}
                    placeholder="$0"
                    readOnly={item.readonly}
                    disabled={item.readonly}
                  />
                </td>
              ))}
            </tr>
          ))}

          {/* LIABILITIES SECTION */}
          <tr className="bg-red-100">
            <td colSpan={years.length + 1} className="p-2 border font-bold text-red-900">
              LIABILITIES
            </td>
          </tr>
          {LIABILITIES_FIELDS.map((item, idx) => (
            <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className={`p-3 border font-medium ${item.readonly ? 'bg-blue-50' : ''} ${item.bold ? 'font-bold' : ''}`}>
                {item.indent && <span className="ml-4">↳ </span>}
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
                {item.readonly && <span className="ml-2 text-xs text-green-600">(Auto)</span>}
              </td>
              {years.map(year => (
                <td key={year} className={`p-2 border ${item.readonly ? 'bg-blue-50' : ''}`}>
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className={`w-full px-2 py-1 border rounded ${
                      item.readonly ? 'bg-gray-100 cursor-not-allowed font-semibold' : ''
                    }`}
                    placeholder="$0"
                    readOnly={item.readonly}
                    disabled={item.readonly}
                  />
                </td>
              ))}
            </tr>
          ))}

          {/* EQUITY SECTION */}
          <tr className="bg-green-100">
            <td colSpan={years.length + 1} className="p-2 border font-bold text-green-900">
              EQUITY
            </td>
          </tr>
          {EQUITY_FIELDS.map((item, idx) => (
            <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className={`p-3 border font-medium ${item.readonly ? 'bg-green-50' : ''} ${item.bold ? 'font-bold' : ''}`}>
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
                {item.readonly && <span className="ml-2 text-xs text-green-600">(Auto)</span>}
              </td>
              {years.map(year => (
                <td key={year} className={`p-2 border ${item.readonly ? 'bg-green-50' : ''}`}>
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className={`w-full px-2 py-1 border rounded ${
                      item.readonly ? 'bg-gray-100 cursor-not-allowed font-semibold' : ''
                    }`}
                    placeholder="$0"
                    readOnly={item.readonly}
                    disabled={item.readonly}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

BalanceSheetForm.propTypes = {
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  financialData: PropTypes.object.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired
};

export default BalanceSheetForm;
